/**
 * The one confirm dialog behind WD-1 … WD-9.
 *
 * Two things it enforces that a bare `Modal.confirm` does not:
 *   • a mandatory reason where a story requires one (US-805 AC1, US-A06 AC5,
 *     US-A07 AC5) — the reason reaches the audit log, so "" is not acceptable;
 *   • type-to-confirm for irreversible destructive actions.
 */
import { Alert, Form, Input, Modal, Typography } from 'antd'
import { useState } from 'react'
import type { ReactNode } from 'react'
import { useAdminTranslate } from '@/providers/i18n-provider'

export interface ConfirmDialogProps {
  open: boolean
  title: string
  /** Explains the consequence in plain language — no "Are you sure?". */
  description: ReactNode
  confirmLabel: string
  cancelLabel?: string
  danger?: boolean
  /** Collect a reason and pass it to onConfirm. Recorded in the audit log. */
  requireReason?: boolean
  reasonLabel?: string
  /** Extra controls (a duration picker, a plan selector) above the reason. */
  children?: ReactNode
  /** The confirm button stays disabled until this exact text is typed. */
  typeToConfirm?: string
  onConfirm: (reason?: string) => Promise<void> | void
  onCancel: () => void
}

export const ConfirmDialog = ({
  open,
  title,
  description,
  confirmLabel,
  cancelLabel,
  danger = false,
  requireReason = false,
  reasonLabel,
  children,
  typeToConfirm,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) => {
  const translate = useAdminTranslate()
  const [reason, setReason] = useState('')
  const [typed, setTyped] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const reasonOk = !requireReason || reason.trim().length >= 5
  const typedOk = !typeToConfirm || typed.trim() === typeToConfirm

  const reset = () => {
    setReason('')
    setTyped('')
    setError(null)
  }

  const handleOk = async () => {
    setSubmitting(true)
    setError(null)
    try {
      await onConfirm(requireReason ? reason.trim() : undefined)
      reset()
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : translate('admin.confirm.actionFailed'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      open={open}
      title={title}
      okText={confirmLabel}
      cancelText={cancelLabel ?? translate('common.cancel')}
      okButtonProps={{ danger, disabled: !reasonOk || !typedOk, loading: submitting }}
      onOk={handleOk}
      onCancel={() => {
        reset()
        onCancel()
      }}
      destroyOnHidden
    >
      <Typography.Paragraph>{description}</Typography.Paragraph>

      {children}

      {requireReason ? (
        <Form layout="vertical">
          <Form.Item
            label={reasonLabel ?? translate('admin.confirm.reasonLabel')}
            required
            validateStatus={reason.length > 0 && !reasonOk ? 'error' : undefined}
            help={
              reason.length > 0 && !reasonOk
                ? translate('admin.confirm.reasonTooShort')
                : translate('admin.confirm.reasonAudited')
            }
          >
            <Input.TextArea
              rows={3}
              value={reason}
              onChange={event => setReason(event.target.value)}
              maxLength={500}
            />
          </Form.Item>
        </Form>
      ) : null}

      {typeToConfirm ? (
        <Form layout="vertical">
          <Form.Item
            label={translate('admin.confirm.typeToConfirm', { word: typeToConfirm })}
            required
          >
            <Input value={typed} onChange={event => setTyped(event.target.value)} />
          </Form.Item>
        </Form>
      ) : null}

      {error ? <Alert type="error" showIcon message={error} /> : null}
    </Modal>
  )
}
